import * as React from 'react';
import { ContextualMenu, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { ArtikelAttachment, updateArtikelAttachment } from 'api/artikelAttachment';
import { FormTextField } from 'formComponents/FormTextField';
import { SavingState } from 'api/savingState';
import { isEqual } from 'lodash';
import { logDifference } from 'utils/helper';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { useIsMounted } from 'hooks/useIsMounted';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { Checkbox } from '@fluentui/react/lib/Checkbox';

const styles = mergeStyleSets({
    data: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: '150px 1fr max-content',
        gridColumnGap: 10,
        minHeight: 200,
    },
});

interface EditArtikelAttachmentProps {
    artikelAttachment: ArtikelAttachment;
    onClose: (refetch: boolean) => void;
}

export const EditArtikelAttachment: React.FC<EditArtikelAttachmentProps> = ({ artikelAttachment, onClose }) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [artikelAttachmentData, setArtikelAttachmentData] = React.useState(artikelAttachment);
    const isMounted = useIsMounted();

    React.useEffect(() => {
        if (savingState === SavingState.notChanged && !isEqual(artikelAttachmentData, artikelAttachment)) {
            setSavingState(SavingState.notSaved);
            logDifference('artikelAttachment', artikelAttachmentData, artikelAttachment);
        }
    }, [savingState, artikelAttachmentData, artikelAttachment]);

    const save = React.useCallback(() => {
        if (!artikelAttachmentData) {
            return;
        }

        if (savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            updateArtikelAttachment(artikelAttachmentData)
                .then(() => {
                    setSavingState(SavingState.saved);
                    if (isMounted) {
                        setSavingState(SavingState.notChanged);
                    }

                    onClose(true);
                })
                .catch((error) => {
                    console.error(error);
                    setSavingState(SavingState.error);
                });
        }
    }, [artikelAttachmentData, isMounted, onClose, savingState]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title="Dokument bearbeiten"
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    layerProps: { eventBubblingEnabled: true },
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={1000}
                onDismiss={() => onClose(false)}
            >
                <form className={formStyles.form}>
                    <div className={styles.data}>
                        <FormDatePicker
                            label="Datum"
                            savingState={savingState}
                            value={artikelAttachmentData.datum}
                            onChange={(newValue) => {
                                setArtikelAttachmentData({
                                    ...artikelAttachmentData,
                                    datum: newValue,
                                });
                            }}
                            options={{
                                required: true,
                            }}
                        />

                        <FormTextField
                            value={artikelAttachmentData.text}
                            savingState={savingState}
                            options={{}}
                            onChange={(newValue): void => {
                                setArtikelAttachmentData({
                                    ...artikelAttachmentData,
                                    text: newValue as string,
                                });
                            }}
                            label={'Beschreibung - ' + artikelAttachmentData.file}
                        />
                        <div>
                            <Checkbox
                                className={formStyles.checkboxInsideFormAligned}
                                label="Mit Angebot versenden"
                                disabled={savingState === SavingState.saving}
                                checked={artikelAttachmentData.sendWithAngebot}
                                onChange={(event, checked) => {
                                    setArtikelAttachmentData({
                                        ...artikelAttachmentData,
                                        sendWithAngebot: !!checked,
                                    });
                                }}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <DefaultButton
                            text="In OneDrive öffnen"
                            disabled={savingState !== SavingState.notChanged || !artikelAttachment.oneDriveUrl}
                            onClick={() => {
                                if (artikelAttachment.oneDriveUrl) {
                                    window.open(artikelAttachment.oneDriveUrl);
                                }
                            }}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged}
                            />
                        )}
                    </DialogFooter>
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                </form>
            </Dialog>
        </>
    );
};
